import type { ListingStage } from '@/api/v4/project-listings.api';
import type { SpotlightCompany } from '@/api/v4/spotlight.api';
import { PopperComponent } from '@/components/popper/popper-component';
import {
  trackSpotlightCardAction,
  type SpotlightCardActionType,
} from '@/lib/mixpanel/mixpanel';
import { paths } from '@/routes/helpers/paths';
import { modalState } from '@/state/modal.state';
import { colors } from '@/theme/colors';
import { Icon } from '@/ui/icons/icon';
import { Stack } from '@/ui/line/line';
import { Logo } from '@/ui/logo/logo';
import { useCurrentUser } from '@/user/use-current-user.query';
import { useUserAccount } from '@/user/use-user-account';
import { prepareLink } from '@/utils/link';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { getSpotlightCompanyStageForTracking } from '../stage-pills/get-spotlight-company-stage-for-tracking';
import { getSpotlightAccountDisplayName } from '../get-spotlight-account-display-name';
import { SpotlightCompanyStagePills } from '../stage-pills/spotlight-company-stage-pills';
import type { SpotlightStagePillItem } from '../stage-pills/spotlight-stage-pill.types';
import {
  AnimatedCompanyCard,
  CardBody,
  CardContent,
  CardFooter,
  CardFooterContent,
  CardHeader,
  CompanyDescription,
  CompanyDescriptionTooltip,
  CompanyNameLink,
  CompanyNameRow,
  ContactActionSlot,
  ContactTeamButton,
  DescriptionSection,
  HiddenTagsPopover,
  MoreTagsTrigger,
  PopoverTagPill,
  SectionPlaceholder,
  StagePillsSlot,
  TagPill,
  TagsRow,
  TagsSection,
  WebsiteLink,
} from './spotlight-company-card.styles';
import { SpotlightCompanyWorkingWith } from './spotlight-company-working-with';

interface SpotlightCompanyCardProps {
  company: SpotlightCompany;
  delayMs: number;
  listingStages: ListingStage[];
}

export const SpotlightCompanyCard = ({
  company,
  delayMs,
  listingStages,
}: SpotlightCompanyCardProps) => {
  const { t } = useTranslation('spotlight');
  const { data: user } = useCurrentUser();
  const account = useUserAccount();
  const accountDisplayName = getSpotlightAccountDisplayName(account);
  const setModal = useSetAtom(modalState);
  const companyWebsite = company.website ? prepareLink(company.website) : null;
  const spotlightStagePills: SpotlightStagePillItem[] = (
    company.spotlightStagePills ?? []
  ).map(pill => ({
    count: pill.count,
    stage: pill.stage as unknown as SpotlightStagePillItem['stage'],
  }));
  const visibleTags: { name: string }[] = [];
  const hiddenTags: { name: string }[] = [];
  const hasStagePills = spotlightStagePills.length > 0 || company.editorsChoice;

  const trackCardAction = (actionType: SpotlightCardActionType) => {
    if (!user) {
      return;
    }

    const companyStage = getSpotlightCompanyStageForTracking({
      editorsChoiceLabel: t('stage.editorsChoice'),
      listingStages,
      pills: spotlightStagePills,
    });

    trackSpotlightCardAction({
      action_type: actionType,
      company_id: company.id,
      company_name: company.name,
      user_id: user.id,
      ...(companyStage !== null ? { company_stage: companyStage } : {}),
    });
  };

  const openContactInnovationTeamModal = () => {
    if (!account?.name) {
      return;
    }

    setModal({
      accountName: accountDisplayName,
      companyName: company.name,
      organizationId: company.id,
      state: 'contactInnovationTeam',
      useSpotlightInquiry: true,
    });
  };

  return (
    <AnimatedCompanyCard $delayMs={delayMs}>
      <CardContent>
        <CardBody direction="column" gap="16px">
          <CardHeader alignItems="flex-start" gap="16px">
            <Logo
              logoUrl={company.logoUrl}
              name={company.name}
              size="56px"
              borderRadius="12px"
            />
            <Stack gap="8px">
              <CompanyNameRow>
                <CompanyNameLink
                  aria-label={t('goToCompanyPage', {
                    companyName: company.name,
                  })}
                  data-role="company-name-link"
                  maxWidth="100%"
                  to={paths.spotlightCompany({ companyId: company.id })}
                  withEllipsis
                  onClick={event => {
                    event.stopPropagation();
                    trackCardAction('savvy_page_clicked');
                  }}
                >
                  {company.name}
                </CompanyNameLink>
                {companyWebsite && (
                  <WebsiteLink
                    data-role="company-website-link"
                    href={companyWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('openCompanyWebsite', {
                      companyName: company.name,
                    })}
                    title={company.website ?? undefined}
                    onClick={event => {
                      event.stopPropagation();
                      trackCardAction('website_clicked');
                    }}
                  >
                    <Icon
                      icon="Globe"
                      width="16"
                      height="16"
                      color={colors.spotlight.text.muted}
                    />
                  </WebsiteLink>
                )}
              </CompanyNameRow>
            </Stack>
          </CardHeader>
          <DescriptionSection>
            {company.keyOffering ? (
              <CompanyDescriptionTooltip
                Component={<CompanyDescription />}
                text={company.keyOffering}
              />
            ) : (
              <SectionPlaceholder aria-hidden="true">
                placeholder
              </SectionPlaceholder>
            )}
          </DescriptionSection>
          <TagsSection $isEmpty={visibleTags.length === 0}>
            {visibleTags.length > 0 ? (
              <TagsRow>
                {visibleTags.map((tag, tagIndex) => (
                  <TagPill key={`${tag.name}-${tagIndex}`}>{tag.name}</TagPill>
                ))}
                {hiddenTags.length > 0 && (
                  <PopperComponent
                    placement="top"
                    visibleElement={
                      <MoreTagsTrigger>+{hiddenTags.length}</MoreTagsTrigger>
                    }
                    PopperContent={
                      <HiddenTagsPopover>
                        {hiddenTags.map((tag, index) => (
                          <PopoverTagPill key={`${tag.name}-hidden-${index}`}>
                            {tag.name}
                          </PopoverTagPill>
                        ))}
                      </HiddenTagsPopover>
                    }
                  />
                )}
              </TagsRow>
            ) : (
              <SectionPlaceholder aria-hidden="true">
                placeholder
              </SectionPlaceholder>
            )}
          </TagsSection>
          <SpotlightCompanyWorkingWith
            workingTeams={company.workingTeams ?? []}
            onTeamsExpanded={() => trackCardAction('teams_expanded')}
          />
        </CardBody>
        <CardFooter>
          <CardFooterContent>
            <StagePillsSlot $hasStagePills={hasStagePills}>
              <SpotlightCompanyStagePills
                isEditorChoice={company.editorsChoice}
                listingStages={listingStages}
                pills={spotlightStagePills}
              />
            </StagePillsSlot>
            <ContactActionSlot>
              <ContactTeamButton
                type="button"
                variant="text"
                onClick={event => {
                  event.stopPropagation();
                  trackCardAction('contact_clicked');
                  openContactInnovationTeamModal();
                }}
              >
                {t('contactInnovationTeam', {
                  accountName: accountDisplayName,
                })}
              </ContactTeamButton>
            </ContactActionSlot>
          </CardFooterContent>
        </CardFooter>
      </CardContent>
    </AnimatedCompanyCard>
  );
};
